import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { invoiceDetailKey } from '../api/use-invoice-detail';
import { CompletePlaceholderDialog } from '../dialogs/complete-placeholder-dialog';
import { CorrectInvoiceDialog } from '../dialogs/correct-invoice-dialog';
import { useInvoiceDrawer } from '../hooks/use-invoice-drawer';
import { invoiceVerbFor } from '../lib/invoice-actions';
import { RowEditDialog, type RowDialogKind } from './bl-invoice-edit-dialog';
import { classifyRow } from './bl-invoice-row-actions';

import type {
  BlContextForTradeInvoice,
  TicketContextForTradeInvoice,
} from '../dialogs/trade-invoice-payload';
import type { InvoiceActionKey } from '../lib/invoice-actions';
import type { InvoiceRow } from '../types';

// The Content group in the detail drawer: one state-routed "Change values"
// verb over the same mechanisms the list row uses — edit the Draft, correct
// the frozen issued payable via the order, or complete a placeholder stub.
// The drawer is mounted app-wide without the BL page's trade context, so the
// edit dialog takes its identity from the row itself (order, BL,
// counterparty, currency) and loads pricing readiness by BL id; rows missing
// the owning order — or, for non-advance kinds, the BL grain the dialogs
// read — don't offer the edit route here and stay manageable from the BL
// page row.

interface ManageTarget {
  readonly kind: RowDialogKind;
  readonly orderId: string;
  readonly side: 'buy' | 'sell';
}

function manageTarget(invoice: InvoiceRow): ManageTarget | null {
  const kind = classifyRow(invoice);
  if (kind === null || invoice.order_id === null || invoice.invoice_direction === null) {
    return null;
  }
  if (kind.kind !== 'advance' && invoice.bl_order_id === null) return null;
  return {
    kind,
    orderId: invoice.order_id,
    side: invoice.invoice_direction === 'Purchase' ? 'buy' : 'sell',
  };
}

// The change-values route the drawer can actually host: the edit route needs
// the manage target's identity, the correct/complete routes only the row.
function hostableRoute(invoice: InvoiceRow, target: ManageTarget | null): InvoiceActionKey | null {
  const verb = invoiceVerbFor(invoice, 'change_values');
  if (verb === null) return null;
  if (verb.routesTo === 'edit' && target === null) return null;
  return verb.routesTo;
}

export function ManageActions({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const target = manageTarget(invoice);
  const route = hostableRoute(invoice, target);
  if (route === null) return null;
  const onChanged = () => {
    void queryClient.invalidateQueries({ queryKey: invoiceDetailKey(invoice.id) });
  };
  return (
    <section aria-labelledby="content-heading" className="space-y-2">
      <h3 id="content-heading" className="text-sm font-semibold">
        CONTENT
      </h3>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        data-testid={`invoice-${invoice.id}-change-values`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Pencil className="size-4" />
        Change values
      </Button>
      <ChangeValuesDialog
        invoice={invoice}
        route={route}
        target={target}
        open={open}
        onOpenChange={setOpen}
        onChanged={onChanged}
      />
    </section>
  );
}

function ChangeValuesDialog({
  invoice,
  route,
  target,
  open,
  onOpenChange,
  onChanged,
}: Readonly<{
  invoice: InvoiceRow;
  route: InvoiceActionKey;
  target: ManageTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}>) {
  if (route === 'complete_placeholder') {
    return (
      <CompletePlaceholderDialog open={open} onOpenChange={onOpenChange} invoiceId={invoice.id} />
    );
  }
  if (route === 'correct') {
    return (
      <CorrectInvoiceDialog
        open={open}
        onOpenChange={onOpenChange}
        invoice={invoice}
        onCorrected={onChanged}
      />
    );
  }
  if (target === null) return null;
  return (
    <RowEditDialog
      kind={target.kind}
      invoice={invoice}
      side={target.side}
      orderId={target.orderId}
      blOrderId={invoice.bl_order_id ?? ''}
      ticket={ticketContextFromRow(invoice)}
      bl={blContextFromRow(invoice)}
      open={open}
      onOpenChange={onOpenChange}
      onSaved={onChanged}
    />
  );
}

// Kept for the discard flow hosted by the lifecycle section: deleting a row
// from the drawer closes it, since the row no longer exists.
export function useDrawerDiscard(invoice: InvoiceRow): () => void {
  const queryClient = useQueryClient();
  const { close } = useInvoiceDrawer();
  return () => {
    void queryClient.invalidateQueries({ queryKey: invoiceDetailKey(invoice.id) });
    close();
  };
}

// The display-context slices of the edit dialogs, hydrated from what the
// invoice row itself carries. Ticket-only pricing extras stay null and render
// as em-dashes; pricing readiness is fetched by BL id, not read from here.
function ticketContextFromRow(invoice: InvoiceRow): TicketContextForTradeInvoice {
  return {
    currency: invoice.currency === 'USD' || invoice.currency === 'EUR' ? invoice.currency : null,
    counterpartyName: invoice.company_name,
    paymentTerms: null,
    pricing_type: null,
    signed_price: null,
    lme_price: null,
    premium_discount: null,
    payable_percent: null,
  };
}

function blContextFromRow(invoice: InvoiceRow): BlContextForTradeInvoice {
  return {
    shippedQuantityMt: null,
    contractNumber: null,
    blOrderName: invoice.bl_order_name,
    blNumber: null,
  };
}
